/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Modal,
  message,
  DatePicker,
  Card,
  TreeSelect,
} from 'antd';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import createClave from 'utils/keyGenerator';
import { isMutable, isAuthorized } from 'utils/estadosGlobales';
import { ActionsNames, hasActionPermission } from 'utils/permission/index';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import NumericInput from './NumericInput';

export const defaultFields = {
  clave: {
    name: 'clave',
    label: 'Clave',
    required: true,
    colWidth: 150,
    isKey: true,
  },
  nombre: {
    name: 'nombre',
    label: 'Nombre',
    required: true,
    colWidth: 200,
  },
  concepto: {
    name: 'concepto',
    label: 'Concepto',
    required: true,
    colWidth: 200,
  },
  descripcion: {
    name: 'descripcion',
    label: 'Descripción',
    required: true,
    colWidth: 200,
  },
  observaciones: {
    name: 'observaciones',
    label: 'Observaciones',
    required: true,
    breakpoints: { md: 16 },
  },
};

const PlainCRUD = ({
  title,
  get,
  post,
  patch,
  delete: _delete,
  fields,
  withoutState,
  permission,
  readOnly,
  catalogs: fksCatalogs,
  tableProps,
  valuesToFormFormatter,
  initialValues,
  allowImport,
  baseURI,
}) => {
  const inputRef = useRef();
  const [form] = Form.useForm();
  const estadosGlobales = useSelector(({ catalogs }) => catalogs.estadosGlobales);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [mutable, setMutable] = useState(true);
  const [hasAllViewPermission, setHasAllViewPermission] = useState(true);
  const [catalogs, setCatalogs] = useState({});
  const [plainCatalogs, setPlainCatalogs] = useState({});

  const [isPaginated, setIsPaginated] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const columns = [
    ...fields.filter((e) => e?.colWidth)
      .map((f) => {
        const catalog = f?.useCatalog
          ? { cat: plainCatalogs?.[f.name], labelProp: f.labelProp || 'descripcion' } : null;
        return {
          titleText: f.label,
          key: f.name,
          dataIndex: f.name,
          width: f.colWidth,
          align: f.align,
          render: f.render,
          ellipsis: f.colWidth >= 350,
          catalog,
        };
      }),
    !withoutState && {
      titleText: 'Estado',
      key: 'estados_globales',
      dataIndex: 'estados_globales',
      width: 150,
      // render: (val) => estadosGlobales.find((e) => e.id === val)?.descripcion,
      catalog: {
        cat: estadosGlobales,
        labelProp: 'descripcion',
      },
    },
  ].filter((e) => e);

  const fetchData = async (params = null) => {
    setLoading(true);
    const _data = await get(params);
    const _isPaginated = !Array.isArray(_data);
    setIsPaginated(_isPaginated);
    setData(_data);
    if (fields.some((e) => e.useCatalog)) {
      const _plainCatalogs = Object.fromEntries(
        fksCatalogs
          .map(({ formItemName: catalogName, extractor }) => [
            catalogName,
            extractor?.(catalogs[catalogName]) || catalogs[catalogName],
          ]),
      );
      setPlainCatalogs(_plainCatalogs);
      setTableData(_data);
    } else {
      setTimeout(() => setTableData(_data));
    }
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const validatePermission = () => {
      const _hasAllViewPermission = fksCatalogs
        .map((c) => hasActionPermission(c.permission, ActionsNames.view))
        .every((e) => e);
      if (!_hasAllViewPermission) {
        message.warn('Hacen falta permisos en los catálogos relacionados a este módulo');
      }
      if (mounted) {
        setHasAllViewPermission(_hasAllViewPermission);
      }
    };

    const fetchAll = async () => {
      setLoading(true);
      const results = await (fksCatalogs.length
        ? Promise.all(fksCatalogs.map((e) => e.get())) : null);
      const _data = await get();
      const _isPaginated = !Array.isArray(_data);
      setIsPaginated(_isPaginated);
      const _catalogs = fksCatalogs.length
        ? Object.fromEntries(results.map((e, idx) => [fksCatalogs[idx].formItemName, e])) : null;
      if (fksCatalogs.length) {
        setCatalogs(_catalogs);
      }
      if (fields.some((e) => e.useCatalog)) {
        const _plainCatalogs = Object.fromEntries(
          fksCatalogs
            .map(({ formItemName: catalogName, extractor }) => [
              catalogName,
              extractor?.(_catalogs?.[catalogName]) || _catalogs?.[catalogName],
            ]),
        );
        setPlainCatalogs(_plainCatalogs);
        setTableData(_data);
      } else {
        setTimeout(() => setTableData(_data));
      }
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    validatePermission();
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setMutable(true);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const success = await (key ? patch(key, values) : post(values));
      if (success) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await _delete(key);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const handleOnRowClick = (_record) => {
    const keyProp = tableProps?.expandable ? 'key' : 'id';
    const key = _record[keyProp];
    setSelectedRowKeys([key]);
    const record = (isPaginated ? data.results : data).find((e) => e[keyProp] === key) || _record;
    setMutable(isMutable(record));
    form.setFieldsValue(valuesToFormFormatter(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    const keyField = fields.find((f) => f.isKey);
    if (keyField?.autoGenerate && !isPaginated) {
      createClave(data, form, { keyProp: keyField.name });
    }
    setVisible(true);
  };

  const onClickEdit = () => setVisible(true);

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    const match = (isPaginated ? data.results : data).find((e) => e.id === key);
    if (isMutable(match)) {
      setVisibleAlert(true);
    } else {
      message.warn(`No se puede eliminar un ${title} ${isAuthorized(match) ? 'autorizado' : 'cancelado'}`);
    }
  };

  const onClickExpand = () => setVisible(true);

  const rules = {
    required: [{ required: true }],
  };

  const controls = () => {
    if (readOnly) {
      return {
        onClickExpand,
      };
    }
    return {
      onClickAdd,
      onClickEdit,
      onClickDelete,
    };
  };

  const getTitle = () => {
    if (readOnly) {
      return 'Vista previa';
    }
    return `${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${title}`;
  };

  const getRules = (field) => {
    if (readOnly || !mutable) {
      return null;
    }
    return field.rules || (field.required && rules.required) || null;
  };

  useEffect(() => {
    if (visible) {
      // setTimeout(inputRef.current.focus);
    }
  }, [visible]);

  const generateKey = (val, catalog, filterProp) => {
    const keyField = fields.find((f) => f.isKey);
    if (val && !isPaginated) {
      const parent = catalog.find((e) => e.value === val);
      createClave(data, form, { keyProp: keyField.name, filter: { parent, prop: filterProp } });
    } else {
      form.resetFields([keyField.name]);
    }
  };

  const disabled = !mutable || readOnly;
  const useModal = fields.length < 6;

  const getControl = (field, useInputRef) => {
    if (field.type === 'textarea') {
      return (
        <Input.TextArea
          allowClear
          ref={useInputRef ? inputRef.current : null}
          autoSize={field.autoSize}
          disabled={disabled}
        />
      );
    }
    if (field.type === 'treeselect') {
      return (
        <TreeSelect
          onChange={field.manageKey
            ? (val) => generateKey(val, plainCatalogs[field.name], field.name) : null}
          showSearch
          treeNodeFilterProp="title"
          style={{ width: '100%' }}
          dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
          treeData={catalogs[field.name]}
          allowClear
          disabled={disabled}
        />
      );
    }
    if (field.type === 'select') {
      return (
        <Select
          onChange={field.manageKey
            ? (val) => generateKey(val, plainCatalogs[field.name], field.name) : null}
          render={field.render}
          dataSource={catalogs[field.name]}
          trueFalse={!catalogs[field.name]}
          disabled={disabled}
        />
      );
    }
    if (field?.type?.includes('date')) {
      return (
        <DatePicker
          picker={field.type.replace('date.', '')}
          format={field.type === 'date' ? DATE_FRONTEND_FORMAT : null}
          placeholder=""
          disabled={disabled}
        />
      );
    }
    return (
      <Input
        allowClear
        ref={useInputRef ? inputRef.current : null}
        disabled={disabled}
      />
    );
  };

  const getItem = (field, useInputRef) => {
    if (field.type === 'decimal') {
      return (
        <Col {...(useModal ? { span: 24 } : { xs: 24, sm: 24, md: 8 })}>
          <NumericInput
            name={field.name}
            label={field.label}
            disabled={disabled}
            // ref={_inputRef}
            required={field.required}
            decimal
          />
        </Col>
      );
    }

    return (
      <Col {...(useModal ? { span: 24 } : { xs: 24, sm: 24, md: field.breakpoints?.md || 8 })}>
        <Form.Item
          name={field.name}
          label={field.label}
          rules={getRules(field, useInputRef)}
          hasFeedback
        >
          {getControl(field, useInputRef)}
        </Form.Item>
      </Col>
    );
  };

  const MyForm = () => (
    <Form
      layout="vertical"
      form={form}
      onFinish={() => onFinish()}
      scrollToFirstError
      className={disabled ? 'without-feedback' : ''}
      initialValues={{
        estados_globales: 1,
        ...initialValues,
      }}
      style={{ display: visible ? 'initial' : 'none' }}
    >
      <Row gutter={10}>
        {fields.map((field, idx) => (
          <React.Fragment key={field.name}>
            {getItem(field, idx === 0 ? inputRef.current : null)}
          </React.Fragment>
        ))}
        {!withoutState && (
          <Col {...(useModal ? { span: 24 } : { xs: 24, sm: 24, md: 8 })}>
            <Form.Item
              name="estados_globales"
              label="Estado"
              rules={readOnly || !mutable ? null : rules.required}
              hasFeedback
            >
              <Select
                disabled={!selectedRowKeys.length || !mutable || readOnly}
                dataSource={estadosGlobales}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
      <Form.Item name="id" hidden>
        <Input />
      </Form.Item>
      <Form.Item hidden>
        <Button htmlType="submit" />
      </Form.Item>
    </Form>
  );

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {(useModal || (!useModal && !visible)) && (
          <Table
            cols={columns}
            data={tableData.results}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={controls()}
            mobileColIndex={0}
            permission={permission}
            hasAllViewPermission={hasAllViewPermission}
            rowKey={tableProps.expandable ? 'key' : 'id'}
            allowExpand={readOnly}
            pagination={!isPaginated}
            allowImport={allowImport}
            baseURI={baseURI}
            count={isPaginated && tableData.count}
            currentPage={isPaginated && currentPage}
            paginationCallback={async (page, q) => {
              if (isPaginated) {
                setCurrentPage(page);
                await fetchData({ page, ...q });
              }
            }}
            {...tableProps}
          />
        )}
        {!useModal && visible && (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} ${title}`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                permission={permission}
              />
            )}
            bordered={false}
          >
            <MyForm />
          </Card>
        )}
        {useModal && (
          <Modal
            visible={visible}
            title={(
              <FormSubmitControls
                label={getTitle()}
                onFinish={!readOnly || mutable ? () => onFinish() : null}
                onCancel={onCancel}
                loading={loading}
                mutable={mutable}
                permission={permission}
              />
            )}
            onCancel={onCancel}
            footer={null}
            closable={false}
            maskClosable={!loading}
            keyboard={!loading}
            forceRender
          >
            <MyForm />
          </Modal>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content={`${title} ${form.getFieldValue(fields[0]?.name || 'descripcion')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

PlainCRUD.propTypes = {
  title: PropTypes.string.isRequired,
  readOnly: PropTypes.bool,
  allowImport: PropTypes.bool,
  baseURI: PropTypes.string,
  withoutState: PropTypes.bool,
  get: PropTypes.func,
  post: PropTypes.func,
  patch: PropTypes.func,
  delete: PropTypes.func,
  fields: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf(['select', 'treeselect', 'decimal', 'textarea', 'date', 'date.year']),
    colWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    colAlign: PropTypes.oneOf(['left', 'right']),
    autoSize: PropTypes.shape({
      minRows: PropTypes.number,
      maxRows: PropTypes.number,
    }),
    breakpoints: PropTypes.shape({
      md: PropTypes.number,
    }),
    isKey: PropTypes.bool,
    autoGenerate: PropTypes.bool,
    manageKey: PropTypes.bool,
  })),
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    authorize: PropTypes.bool.isRequired,
    cancel: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    sync: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }),
  catalogs: PropTypes.arrayOf(PropTypes.shape({
    permission: PropTypes.string,
    get: PropTypes.func.isRequired,
    formItemName: PropTypes.string.isRequired,
    extractor: PropTypes.func,
  })),
  tableProps: PropTypes.shape({
    pageSize: PropTypes.number,
    expandable: PropTypes.oneOfType([PropTypes.bool, PropTypes.shape()]),
  }),
  valuesToFormFormatter: PropTypes.func,
  initialValues: PropTypes.shape(),
};

PlainCRUD.defaultProps = {
  readOnly: false,
  withoutState: false,
  allowImport: false,
  baseURI: '',
  fields: [defaultFields.descripcion],
  get: null,
  post: null,
  patch: null,
  delete: null,
  permission: null,
  catalogs: [],
  tableProps: {},
  valuesToFormFormatter: (values) => values,
  initialValues: {},
};

export default PlainCRUD;
